import { combineLatest } from 'rxjs';
import {
  action$,
  combineReducers,
  reducer,
  streamReducer,
} from '@ardoq/rxbeach';
import { context$ } from 'streams/context/context$';
import {
  APIFieldAttributes,
  APIFieldType,
  ArdoqId,
  ViewIds,
} from '@ardoq/api-types';
import { SpiderChartStreamedProps } from './types';
import { modelUpdateNotification$ } from 'modelInterface/modelUpdateNotification$';
import { bypassLimitAction } from './actions';
import { isPresentationMode } from 'env';
import { ContextShape } from '@ardoq/data-model';
import { EMPTY_CONTEXT_SHAPE } from 'streams/context/ContextShape';
import { buildViewModel } from './buildViewModel';
import { ExtractStreamShape } from 'tabview/types';
import { focusedComponentChanged } from 'tabview/actions';
import { HasViewInstanceId } from '@ardoq/graph';
import { getWorkspaceModeViewSettings$ } from 'views/environmentals/viewSettings/workspaceModeViewSettings$';
import { getDefaultViewSettings } from 'views/environmentals/viewSettings/getDefaultViewSettings';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { locale$ } from 'streams/locale$';
import { ARDOQ_DEFAULT_LOCALE, type Locale } from '@ardoq/locale';
import { getErrorInfoBoxLabels } from 'locales/getErrorInfoBoxLabels';
import {
  components$,
  ComponentsState,
  toComponentsState,
} from 'streams/components/components$';
import {
  fieldsByWorkspaceId$,
  FieldsByWorkspaceId,
} from 'streams/fields/fieldsByWorkspaceId$';
import { componentInterface } from '@ardoq/component-interface';

const getIncludedComponentIds = (components: ComponentsState): Set<ArdoqId> =>
  new Set(
    components.models
      .filter(component =>
        componentInterface.isIncludedInContextByFilter(component)
      )
      .map(component => component._id)
  );

const getNumericFields = (
  fieldsByWorkspaceId: FieldsByWorkspaceId,
  workspaceId: ArdoqId
): APIFieldAttributes[] =>
  (fieldsByWorkspaceId[workspaceId] ?? []).filter(
    field => field.type === APIFieldType.NUMBER
  );

type SpiderChartViewStreamState = SpiderChartStreamedProps & {
  context: ContextShape;
  components: ComponentsState;
  locale: Locale;
};

const defaultViewSettings = getDefaultViewSettings(ViewIds.SPIDER, false);

const emptyState: SpiderChartViewStreamState = {
  viewSettings: defaultViewSettings,
  context: EMPTY_CONTEXT_SHAPE,
  components: toComponentsState([]),
  viewModel: {
    sideways: false,
    numericFields: [],
    selectedFields: [],
    componentId: '',
    chartPoints: [],
    legendRows: [],
    focusedComponentId: null,
    errors: [],
  },
  i18nLocale: {},
  locale: ARDOQ_DEFAULT_LOCALE,
  errorInfoBoxLabels: getErrorInfoBoxLabels({}),
};

const reset = ({
  context,
  components,
  viewSettings,
  viewModel,
  i18nLocale,
  locale,
  errorInfoBoxLabels,
}: SpiderChartViewStreamState): SpiderChartViewStreamState => {
  if (!context.workspaceId) {
    return { ...emptyState, i18nLocale, locale, errorInfoBoxLabels };
  }

  return {
    context,
    components,
    viewSettings,
    viewModel,
    i18nLocale,
    locale,
    errorInfoBoxLabels,
  };
};

const handleFocusedComponentChanged = (
  state: SpiderChartViewStreamState,
  focusedComponentId: ArdoqId | null
): SpiderChartViewStreamState => ({
  ...state,
  viewModel: {
    ...state.viewModel,
    focusedComponentId:
      focusedComponentId !== state.viewModel.focusedComponentId
        ? focusedComponentId
        : null,
  },
});
export const getViewModel$ = ({ viewInstanceId }: HasViewInstanceId) => {
  const reset$ = combineLatest([
    getWorkspaceModeViewSettings$(ViewIds.SPIDER, viewInstanceId),
    context$,
    components$,
    i18nLocale$,
    locale$,
    fieldsByWorkspaceId$,
    modelUpdateNotification$,
  ]);
  const resetReducer = (
    previousState: SpiderChartViewStreamState,
    [
      viewSettings,
      context,
      components,
      i18nLocale,
      locale,
      fieldsByWorkspaceId,
    ]: ExtractStreamShape<typeof reset$>
  ) => {
    return reset({
      context,
      components,
      viewSettings,
      i18nLocale,
      locale,
      errorInfoBoxLabels: getErrorInfoBoxLabels(i18nLocale),
      viewModel: buildViewModel({
        componentId: context.componentId,
        workspaceId: context.workspaceId,
        focusedComponentId: previousState.viewModel.focusedComponentId,
        viewSettings,
        bypassLimit: isPresentationMode(),
        i18nLocale,
        locale,
        components,
        numericFields: getNumericFields(
          fieldsByWorkspaceId,
          context.workspaceId
        ),
        includedComponentIds: getIncludedComponentIds(components),
      }),
    });
  };

  const bypassLimitReducer = (state: SpiderChartViewStreamState) => {
    return {
      ...state,
      viewModel: buildViewModel({
        componentId: state.context.componentId,
        workspaceId: state.context.workspaceId,
        focusedComponentId: state.viewModel.focusedComponentId,
        viewSettings: state.viewSettings,
        bypassLimit: true,
        i18nLocale: state.i18nLocale,
        locale: state.locale,
        components: state.components,
        numericFields: state.viewModel.numericFields,
        includedComponentIds: getIncludedComponentIds(state.components),
      }),
    };
  };

  return action$.pipe(
    combineReducers<SpiderChartViewStreamState>(emptyState, [
      streamReducer(reset$, resetReducer),
      reducer(focusedComponentChanged, handleFocusedComponentChanged),
      reducer(bypassLimitAction, bypassLimitReducer),
    ])
  );
};
